import { makeAutoObservable, observable } from 'mobx';
import { ChipId, ChipInstanceId, createChipInstanceId } from '@/types/ids';
import { Point, PointData } from '@/models/Point';
import { GlobalTilePosition, GlobalTilePositionDto } from '@/types';
import { ChipInstanceDtoSchema } from '@/schemas/chip';
import Context from '@/context';
import type { IChipInstanceModel, IChipModel, ILogicGraph } from '@/interfaces/models';
import { LogicGraph } from './logic/LogicGraph';
import {
  restoreInstanceRuntime,
  cloneLiftedInstance,
  validateChipLift,
} from './logic/restoreInstanceRuntime';
import { LogicGraphDto } from '@/schemas/logic';
import { Rotation } from '@/models/data/Rotation';
import type {
  GraphSnapshot,
  InstanceSnapshot,
  ProjectSnapshotBuilder,
} from '@/services/projectSnapshot/ProjectSnapshot';
import { liveGraphRecords } from '@/services/projectSnapshot/GraphRecordSource';
import { RuntimeBindings } from './logic/RuntimeBindings';

export interface ChipInstanceDto {
  id: ChipInstanceId;
  chipId: ChipId;
  originPointDto: PointData;
  logicGraphDto: LogicGraphDto;
  originGlobalTileLocation?: GlobalTilePositionDto;
  templateToInstanceIdMap?: Record<string, string>;
  rotation?: number;
  bundleWidth?: number;
}

/** Chip instance model copied for each new chip instance mainly for the logicGraph */
export class ChipInstanceModel implements IChipInstanceModel {
  #nativeSnapshotModel = undefined;
  /** Brand check rejects opaque proxies before inspecting any snapshot hooks. */
  static isNativeSnapshotModel(value: unknown): value is ChipInstanceModel {
    return (
      !!value &&
      typeof value === 'object' &&
      #nativeSnapshotModel in value &&
      Object.getPrototypeOf(value) === ChipInstanceModel.prototype
    );
  }

  #editingGraphOwner = undefined;
  static adoptEditingGraph(instance: IChipInstanceModel, graph: LogicGraph): void {
    if (
      !(#editingGraphOwner in instance) ||
      Object.getPrototypeOf(instance) !== ChipInstanceModel.prototype
    )
      return;
    if (
      !nativeInstanceMethod(
        instance,
        'captureSnapshot',
        ChipInstanceModel.#native_captureSnapshot,
      ) ||
      !nativeInstanceMethod(instance, 'toDto', ChipInstanceModel.#native_toDto) ||
      !nativeInstanceGetter(instance, 'dto', ChipInstanceModel.#native_dto) ||
      !nativeInstanceMethod(
        instance,
        'snapshotMetadata',
        ChipInstanceModel.#native_snapshotMetadata,
      ) ||
      !nativeInstanceMethod(
        instance,
        'trackSnapshotDependencies',
        ChipInstanceModel.#native_trackSnapshotDependencies,
      )
    )
      return;
    if (
      LogicGraph.supportsEditingOwnership(instance.logicGraph) &&
      RuntimeBindings.isNativeEmpty(instance.templateToInstanceIdMap)
    )
      instance.logicGraph = graph;
  }
  static #nativeBindingsToDto = RuntimeBindings.prototype.toDto;
  static #nativeGraphHooks = {
    boundaryNodes: LogicGraph.prototype.boundaryNodes,
    edgeDto: LogicGraph.prototype.edgeDto,
    nodeIdText: LogicGraph.prototype.nodeIdText,
  };
  static #native_captureSnapshot = ChipInstanceModel.prototype.captureSnapshot;
  static #native_toDto = ChipInstanceModel.prototype.toDto;
  static #native_dto = Object.getOwnPropertyDescriptor(
    ChipInstanceModel.prototype,
    'dto',
  )!.get;
  static #native_snapshotMetadata = ChipInstanceModel.prototype.snapshotMetadata;
  static #native_trackSnapshotDependencies =
    ChipInstanceModel.prototype.trackSnapshotDependencies;
  private runtimeInputRevision = 0;
  id = createChipInstanceId();
  rotation: Rotation = Rotation.CW_0;
  bundleWidth?: number;
  templateToInstanceIdMap = new RuntimeBindings();

  static fromDto(dto: ChipInstanceDto, context: Context, logicGraph: ILogicGraph) {
    ChipInstanceDtoSchema.parse(dto);
    const chipInstanceModel = new ChipInstanceModel(
      context,
      dto.chipId,
      Point.fromDto(dto.originPointDto),
      logicGraph,
      dto.originGlobalTileLocation && {
        ...dto.originGlobalTileLocation,
        location: Point.fromDto(dto.originGlobalTileLocation.location),
      },
    );
    chipInstanceModel.id = dto.id;
    chipInstanceModel.bundleWidth = dto.bundleWidth;
    // Factory owns graph hydration. Bind exactly those numeric nodes once.
    chipInstanceModel.templateToInstanceIdMap = RuntimeBindings.fromDto(
      dto.templateToInstanceIdMap,
      logicGraph,
      dto.bundleWidth,
    );
    if (dto.rotation !== undefined) {
      chipInstanceModel.rotation = dto.rotation as Rotation;
    }

    return chipInstanceModel;
  }

  constructor(
    private context: Context,
    public chipId: ChipId,
    public originPoint: Point,
    public logicGraph: ILogicGraph,
    /**
     * This may be undefined if this is the chip being edited.
     */
    public originGlobalTileLocation?: GlobalTilePosition,
  ) {
    // Node runtime is deliberately not observable; serialize it fresh on every save.
    // Binding maps are replaced as a unit by hydration, placement and graph
    // refresh. Observe replacement without wrapping every stable node ID.
    makeAutoObservable(
      this,
      {
        dto: false,
        toDto: false,
        captureSnapshot: false,
        trackSnapshotDependencies: false,
        observeSnapshotInputs: false,
        snapshotMetadata: false,
        templateToInstanceIdMap: observable.ref,
      } as any,
      { autoBind: true },
    );
  }

  get chip(): IChipModel | undefined {
    return this.context.chipStore.getChip(this.chipId);
  }

  notifyRuntimeInputChanged() {
    this.runtimeInputRevision++;
  }

  get dto(): ChipInstanceDto {
    return this.toDto();
  }

  /** Serialize metadata with the authoritative graph, avoiding a discarded snapshot. */
  toDto(graph: ILogicGraph = this.logicGraph, snapshot?: LogicGraphDto): ChipInstanceDto {
    return { ...this.snapshotMetadata(), logicGraphDto: snapshot ?? graph.dto };
  }

  captureSnapshot(
    builder: ProjectSnapshotBuilder,
    graph: ILogicGraph = this.logicGraph,
    snapshot?: GraphSnapshot,
  ): InstanceSnapshot {
    return {
      ...this.snapshotMetadata(),
      logicGraphDto: snapshot ?? builder.graph(liveGraphRecords(graph)),
    };
  }
  observeSnapshotInputs(): boolean {
    const graph = this.logicGraph;
    if (
      !(graph instanceof LogicGraph) ||
      graph.constructor !== LogicGraph ||
      !graph.isTopologyTracked ||
      graph.boundaryNodes !== ChipInstanceModel.#nativeGraphHooks.boundaryNodes ||
      graph.edgeDto !== ChipInstanceModel.#nativeGraphHooks.edgeDto ||
      graph.nodeIdText !== ChipInstanceModel.#nativeGraphHooks.nodeIdText
    )
      return false;
    if (
      this.captureSnapshot !== ChipInstanceModel.#native_captureSnapshot ||
      this.snapshotMetadata !== ChipInstanceModel.#native_snapshotMetadata ||
      this.trackSnapshotDependencies !==
        ChipInstanceModel.#native_trackSnapshotDependencies
    )
      return false;
    if (
      this.templateToInstanceIdMap.constructor !== RuntimeBindings ||
      this.templateToInstanceIdMap.toDto !== ChipInstanceModel.#nativeBindingsToDto
    )
      return false;
    const template = this.chip?.templateGraph;
    if (
      this.templateToInstanceIdMap.totalSize &&
      template &&
      (!(template instanceof LogicGraph) ||
        template.constructor !== LogicGraph ||
        template.nodeIdText !== ChipInstanceModel.#nativeGraphHooks.nodeIdText)
    )
      return false;
    this.trackSnapshotDependencies();
    void this.logicGraph;
    void this.chip?.templateGraph;
    return true;
  }

  trackSnapshotDependencies(): void {
    void this.runtimeInputRevision;
    void this.id;
    void this.bundleWidth;
    void this.chipId;
    void this.rotation;
    void this.originPoint.x;
    void this.originPoint.y;
    if (this.originGlobalTileLocation) {
      void this.originGlobalTileLocation.chipInstanceId;
      void this.originGlobalTileLocation.circuitType;
      void this.originGlobalTileLocation.location.x;
      void this.originGlobalTileLocation.location.y;
    }
    // The identity map is observable.ref, so its reference is the dependency.
    void this.templateToInstanceIdMap;
  }

  private snapshotMetadata(): Omit<ChipInstanceDto, 'logicGraphDto'> {
    // Internal node state is not observable. Explicit user input must wake autosave.
    void this.runtimeInputRevision;
    return {
      id: this.id,
      bundleWidth: this.bundleWidth,
      chipId: this.chipId,
      originPointDto: this.originPoint.data,
      originGlobalTileLocation: this.originGlobalTileLocation
        ? {
            chipInstanceId: this.originGlobalTileLocation
              .chipInstanceId as ChipInstanceId,
            location: {
              x: this.originGlobalTileLocation.location.x,
              y: this.originGlobalTileLocation.location.y,
            },
            circuitType: this.originGlobalTileLocation.circuitType,
          }
        : undefined,
      templateToInstanceIdMap: this.templateToInstanceIdMap.totalSize
        ? this.templateToInstanceIdMap.toDto(this.logicGraph, this.chip?.templateGraph)
        : undefined,
      rotation: this.rotation !== Rotation.CW_0 ? this.rotation : undefined,
    };
  }

  refreshGraph(): void {
    const chip = this.chip;
    if (!chip?.templateGraph) return;
    if (this.bundleWidth) {
      if (validateChipLift(chip.templateGraph, this.bundleWidth)) return;
      const { graph, combinedMap } = cloneLiftedInstance(
        this,
        chip.templateGraph,
        this.bundleWidth,
      );
      this.logicGraph = graph;
      this.templateToInstanceIdMap = combinedMap;
      return;
    }
    const { graph, idMap } = chip.templateGraph.clone();
    restoreInstanceRuntime(this, graph, idMap);
    this.logicGraph = graph;
    this.templateToInstanceIdMap = idMap;
  }
}

function nativeInstanceMethod(
  instance: ChipInstanceModel,
  name: string,
  method: unknown,
): boolean {
  const descriptor =
    Object.getOwnPropertyDescriptor(instance, name) ??
    Object.getOwnPropertyDescriptor(ChipInstanceModel.prototype, name);
  return !!descriptor && 'value' in descriptor && descriptor.value === method;
}

function nativeInstanceGetter(
  instance: ChipInstanceModel,
  name: string,
  getter: unknown,
): boolean {
  const descriptor =
    Object.getOwnPropertyDescriptor(instance, name) ??
    Object.getOwnPropertyDescriptor(ChipInstanceModel.prototype, name);
  return !!descriptor && descriptor.get === getter && descriptor.set === undefined;
}
